import { notification } from 'antd';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useLazyGetUserAccessRightsQuery } from '@app/api/AccessRights';
import {
  EDIT_EMPLOYEE_PATH,
  FRONT_WARNING,
  RIGHTS_CLIENT_SERVICES,
  RIGHTS_IS_ATTACH,
} from '@app/constants';
import { DESKTOP } from '@app/constants/devicesWidth';
import {
  ACCESS_RIGHTS_DATA,
  ADDITIONAL_INFO_DATA,
  INFO_DATA,
  SERVICE_DATA,
  SETTINGS_DATA,
} from '@app/constants/tabsNames';
import { initialAccessRights } from '@app/data';
import {
  useAccessRights,
  useMemoizeObject,
  useUserPrint,
  useUsersAccessRights,
  useWindowSize,
} from '@app/hooks';
import { NotificationContext, TabsContext } from '@app/providers';
import { usePageContext } from '@app/providers/PageProvider/usePageContext';
import type { NAccessRights } from '@app/types';
import type { NEmployees } from '@app/types/Employees';
import type { UserAccessRightsInterface } from '@app/types/NewAccessRight';

import { useEditEmployeeEffects } from '@pages/Employees/EditEmployee/hooks/useEditEmployeeEffects';

import {
  COPYING_SERVICES_FROM_EMPLOYEE_PARAM,
  type ServicesLoadState,
} from '@pages/Employees/EditEmployee/data/copyingServices';

export const useEditEmployee = () => {
  const [isGlobalLoader, setGlobalLoader] = useState(true);
  const [employee, setEmployee] =
    useState<NEmployees.EditEmployeeInfoTabProps['employee']>();
  const [activeKey, setActiveKey] = useState('1');
  const [infoData, setInfoData] = useState(employee);
  const [allServices, setAllServices] = useState<
    NEmployees.ModelClientServiceWithOptionalProps[]
  >([]);
  const [allSettings, setAllSettings] =
    useState<NEmployees.ModelUserSettings>();
  const [accessRights, setAccessRights] =
    useState<NAccessRights.ModelAccessRights>(() => initialAccessRights);
  const [initialAccessRightData, setInitialAccessRightData] =
    useState<NAccessRights.ModelAccessRights>(() => initialAccessRights);
  const [isNeedToUpdateUserPreview, setIsNeedToUpdateUserPreview] =
    useState(false);
  const [editableUserAccessRights, setEditableUserAccessRights] =
    useState<UserAccessRightsInterface | null>(null);
  const [editableUserUnsavedAccessRights, setEditableUserUnsavedAccessRights] =
    useState<UserAccessRightsInterface | null>(null);
  const [servicesLoadState, setServicesLoadState] = useState<ServicesLoadState>(
    {
      status: 'loading',
      isCopying: false,
    }
  );
  const [handleUnsavedChanges, setHandleUnsavedChanges] =
    useState<NEmployees.HandleUnsavedEmployeeInfoTab | null>(null);

  const [, contextHolder] = notification.useNotification();

  const [searchParams] = useSearchParams();
  const editableEmployeeId = Number(searchParams.get('id'));
  const initialTab = Number(searchParams.get('key'));
  const copyingServicesFromEmployeeId = Number(
    searchParams.get(COPYING_SERVICES_FROM_EMPLOYEE_PARAM)
  );

  const { userId, database, activeBranchId } = useUserPrint();

  const { innerWidth } = useWindowSize();

  const { showNotification } = useContext(NotificationContext);

  const isDesktop = innerWidth > DESKTOP;

  const navigate = useNavigate();

  const { setOriginalData, setNewData, newData, setTabInfo } =
    useContext(TabsContext);
  const { setTitleData } = usePageContext();

  const { hasUserRightToViewAccessRightTab } = useUsersAccessRights();
  const { hasUserRight: hasUserRightToAttachServices } = useAccessRights(
    RIGHTS_CLIENT_SERVICES,
    RIGHTS_IS_ATTACH
  );
  const hasCurrentUserRightToViewAllRecords = useMemo(() => {
    return editableUserAccessRights?.Records.IsViewAllRecords;
  }, [editableUserAccessRights]);

  const memoizedAllSettings = useMemoizeObject(allSettings);
  const memoizedInitialAccessRights =
    useMemoizeObject(initialAccessRightData) || initialAccessRights;

  const [getUserAccessRights, { isLoading: isGetUserAccessRightsLoading }] =
    useLazyGetUserAccessRightsQuery();

  const getUserAccessRightsHandler = useCallback(async () => {
    if (!editableEmployeeId) {
      return showNotification(
        'Не удалось получить идентификатор пользователя',
        FRONT_WARNING
      );
    }

    if (!activeBranchId || !hasUserRightToViewAccessRightTab) {
      return;
    }

    try {
      const response = await getUserAccessRights({
        forUserId: editableEmployeeId,
        ActiveBranchId: activeBranchId,
      }).unwrap();

      setEditableUserAccessRights(response);
      setEditableUserUnsavedAccessRights(response);
    } catch (error) {
      showNotification(error);
    }
  }, [
    editableEmployeeId,
    activeBranchId,
    hasUserRightToViewAccessRightTab,
    showNotification,
    getUserAccessRights,
  ]);

  const { getData: getEmployeeData } = useEditEmployeeEffects({
    userId,
    database,
    activeBranchId,
    initialTab,
    editableEmployeeId,
    copyingServicesFromEmployeeId,
    employee,
    allServices,
    activeKey,
    accessRights,
    memoizedAllSettings,
    memoizedInitialAccessRights,
    setActiveKey,
    setEmployee,
    setTitleData,
    setAllSettings,
    setAllServices,
    setServicesLoadState,
    showNotification,
    setGlobalLoader,
    setInfoData,
    setOriginalData,
    setNewData,
    setTabInfo,
    getUserAccessRightsHandler,
  });

  const navigateToOtherTab = useCallback(
    (key: string) => {
      if (employee) {
        setActiveKey(key);
        navigate(`${EDIT_EMPLOYEE_PATH}?id=${employee.id}&key=${key}`);
      }
    },
    [employee, navigate]
  );

  const registerHandleUnsavedChanges = useCallback(
    (handler: NEmployees.HandleUnsavedEmployeeInfoTab | null) => {
      setHandleUnsavedChanges(() => handler);
    },
    []
  );

  const handleTabClick = useCallback(
    async (key: string) => {
      if (key === activeKey) {
        return;
      }

      if (handleUnsavedChanges) {
        const canLeave = await handleUnsavedChanges();

        if (!canLeave) {
          return;
        }
      }

      const tabData: Record<string, string> = {
        '2': INFO_DATA,
        '3': ADDITIONAL_INFO_DATA,
        '4': SETTINGS_DATA,
        '5': SERVICE_DATA,
        '8': ACCESS_RIGHTS_DATA,
      };

      setActiveKey(key);
      navigateToOtherTab(key);

      const info = tabData[key];
      if (info) {
        setTabInfo(info);
      }
    },
    [activeKey, handleUnsavedChanges, navigateToOtherTab, setTabInfo]
  );

  return {
    isDesktop,
    contextHolder,
    isGlobalLoader,
    employee,
    activeKey,
    setActiveKey,
    navigateToOtherTab,
    infoData,
    setInfoData,
    setNewData,
    newData,
    setTabInfo,
    handleTabClick,
    allServices,
    setAllServices,
    allSettings,
    setEmployee,
    accessRights,
    setAccessRights,
    initialAccessRightData: memoizedInitialAccessRights,
    setInitialAccessRightData,
    hasUserRightToViewAccessRightTab,
    isNeedToUpdateUserPreview,
    setIsNeedToUpdateUserPreview,
    hasUserRightToAttachServices,
    editableUserAccessRights,
    editableUserUnsavedAccessRights,
    setEditableUserUnsavedAccessRights,
    isGetUserAccessRightsLoading,
    setEditableUserAccessRights,
    hasCurrentUserRightToViewAllRecords,
    getEmployeeData,
    servicesLoadState,
    setHandleUnsavedChanges: registerHandleUnsavedChanges,
  };
};
